import { useState } from 'react';
import reactTextareaAutosize from 'react-textarea-autosize';

import {
  DEFAULT_SUPPORT_MESSAGE_NOT_FOUND,
  DEFAULT_SUPPORT_MESSAGE_NOT_HELPFUL,
} from '@core/const/const.DEFAULT_SUPPORT_MESSAGES';
import { SettingsLabelsEndpoint } from '@core/types/types.endpoint.settings';
import { Button } from '@frontend/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@frontend/components/ui/card';
import { Separator } from '@frontend/components/ui/separator';
import { Textarea } from '@frontend/components/ui/textarea';
import { useDirectoryUpdater } from '@frontend/shared/hooks/shared.hook.useDirectoryUpdater';
import { useFlag } from '@frontend/shared/hooks/shared.hook.useFlag';
import { useDirectoryTeam } from '@frontend/shared/hooks/useDirectory';
import { useSubmitter } from '@frontend/shared/hooks/useSubmitter';
import { callApi } from '@frontend/shared/util/util.callApi';

interface SettingsCustomLabelsProps {
  isReadOnly?: boolean;
}

export function SettingsCustomLabels({
  isReadOnly = false,
}: SettingsCustomLabelsProps) {
  // Check if support mode is enabled
  const supportModeEnabled = useFlag('support_mode');
  // State for editing mode
  const [editing, setEditing] = useState(false);

  // If support mode is not enabled, don't render anything
  if (!supportModeEnabled) {
    return null;
  }

  return (
    <Card className="mt-12 max-w-[600px]">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Custom Labels</CardTitle>
          {!isReadOnly && (
            <Button
              size="sm"
              className="shrink-0 px-3"
              onClick={() => setEditing(true)}
              style={{
                display: editing ? 'none' : 'flex',
              }}
            >
              Edit Labels
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <SupportMessageSettings editing={editing} setEditing={setEditing} />
      </CardContent>
    </Card>
  );
}

function SupportMessageSettings({
  editing,
  setEditing,
}: {
  editing: boolean;
  setEditing: (editing: boolean) => void;
}) {
  const team = useDirectoryTeam();
  const updater = useDirectoryUpdater();

  // Get the current support messages from team or use defaults
  const currentNotFoundMessage =
    team?.labels?.support_message_not_found ??
    DEFAULT_SUPPORT_MESSAGE_NOT_FOUND;

  const currentNotHelpfulMessage =
    team?.labels?.support_message_not_helpful ??
    DEFAULT_SUPPORT_MESSAGE_NOT_HELPFUL;

  // State for the editable messages - use defaults if current values are empty
  const [notFoundMessage, setNotFoundMessage] = useState(
    currentNotFoundMessage || DEFAULT_SUPPORT_MESSAGE_NOT_FOUND,
  );
  const [notHelpfulMessage, setNotHelpfulMessage] = useState(
    currentNotHelpfulMessage || DEFAULT_SUPPORT_MESSAGE_NOT_HELPFUL,
  );

  // Check if any message has been modified and is not empty
  const hasChanges =
    (notFoundMessage !== currentNotFoundMessage &&
      notFoundMessage.trim() !== '') ||
    (notHelpfulMessage !== currentNotHelpfulMessage &&
      notHelpfulMessage.trim() !== '');

  // Update all messages at once
  const updateMessages = useSubmitter(async () => {
    if (!hasChanges) {
      return;
    }

    // Prepare messages for API
    let notFoundToSet = notFoundMessage.trim();
    let notHelpfulToSet = notHelpfulMessage.trim();

    // Don't allow empty values - use defaults instead
    if (notFoundToSet === '') {
      notFoundToSet = DEFAULT_SUPPORT_MESSAGE_NOT_FOUND;
    }
    if (notHelpfulToSet === '') {
      notHelpfulToSet = DEFAULT_SUPPORT_MESSAGE_NOT_HELPFUL;
    }

    // Reset to empty string if default value is used (to save space in DB)
    if (notFoundToSet === DEFAULT_SUPPORT_MESSAGE_NOT_FOUND) {
      notFoundToSet = '';
    }
    if (notHelpfulToSet === DEFAULT_SUPPORT_MESSAGE_NOT_HELPFUL) {
      notHelpfulToSet = '';
    }

    // Create a single labels object with all values
    const labels = {
      support_message_not_found: notFoundToSet,
      support_message_not_helpful: notHelpfulToSet,
      // Include legacy field for backward compatibility
      support_message: notFoundToSet,
    };

    // Make a single API call with the complete labels object
    const [, res] = await callApi<SettingsLabelsEndpoint>('/settings/labels', {
      labels,
    });

    if (res) {
      updater({ team: res });
    }

    setEditing(false);
  });

  // Cancel editing and reset to current values or defaults if empty
  const cancelEditing = () => {
    setEditing(false);
    setNotFoundMessage(
      currentNotFoundMessage || DEFAULT_SUPPORT_MESSAGE_NOT_FOUND,
    );
    setNotHelpfulMessage(
      currentNotHelpfulMessage || DEFAULT_SUPPORT_MESSAGE_NOT_HELPFUL,
    );
  };

  return (
    <div>
      <div className="flex flex-col gap-4">
        {/* Not Found Message */}
        <div>
          <h3 className="mb-1 text-xs font-semibold">
            Initial Support Message
          </h3>
          <p className="mb-2 text-xs font-medium text-gray-500">
            Displayed when an answer is not found initially and support is
            needed
          </p>

          {!editing ? (
            <p
              className={`whitespace-pre-line py-1 text-sm font-normal italic ${
                currentNotFoundMessage !== DEFAULT_SUPPORT_MESSAGE_NOT_FOUND
                  ? 'text-inherit'
                  : 'text-gray-400'
              }`}
            >
              {currentNotFoundMessage}
            </p>
          ) : (
            <div>
              <Textarea
                // @ts-expect-error - reactTextareaAutosize typing issue
                as={reactTextareaAutosize}
                value={notFoundMessage}
                className="mb-2 text-sm leading-[21px]"
                onChange={(e) => setNotFoundMessage(e.target.value)}
              />
              <Button
                size="sm"
                variant="outline"
                className="ml-auto block"
                onClick={() =>
                  setNotFoundMessage(DEFAULT_SUPPORT_MESSAGE_NOT_FOUND)
                }
                disabled={notFoundMessage === DEFAULT_SUPPORT_MESSAGE_NOT_FOUND}
              >
                Reset to Default
              </Button>
            </div>
          )}
        </div>

        <Separator />

        {/* Not Helpful Message */}
        <div>
          <h3 className="mb-1 text-xs font-semibold">
            Not Helpful Support Message
          </h3>
          <p className="mb-2 text-xs font-medium text-gray-500">
            Displayed when an answer is provided but marked as not helpful
          </p>

          {!editing ? (
            <p
              className={`whitespace-pre-line py-1 text-sm font-normal italic ${
                currentNotHelpfulMessage !== DEFAULT_SUPPORT_MESSAGE_NOT_HELPFUL
                  ? 'text-inherit'
                  : 'text-gray-400'
              }`}
            >
              {currentNotHelpfulMessage}
            </p>
          ) : (
            <div>
              <Textarea
                // @ts-expect-error - reactTextareaAutosize typing issue
                as={reactTextareaAutosize}
                value={notHelpfulMessage}
                className="mb-2 text-sm leading-[21px]"
                onChange={(e) => setNotHelpfulMessage(e.target.value)}
              />
              <Button
                size="sm"
                variant="outline"
                className="ml-auto block"
                onClick={() =>
                  setNotHelpfulMessage(DEFAULT_SUPPORT_MESSAGE_NOT_HELPFUL)
                }
                disabled={
                  notHelpfulMessage === DEFAULT_SUPPORT_MESSAGE_NOT_HELPFUL
                }
              >
                Reset to Default
              </Button>
            </div>
          )}
        </div>
      </div>

      {editing && (
        <div className="mt-4 flex">
          <Button
            size="sm"
            onClick={updateMessages.submit}
            disabled={!hasChanges || updateMessages.isSubmitting}
          >
            {updateMessages.isSubmitting ? 'Saving...' : 'Save'}
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="ml-2"
            onClick={cancelEditing}
          >
            Cancel
          </Button>
        </div>
      )}
    </div>
  );
}
